/*
  SingleCustomerView.js - Admin Customer Single View Main Component
*/

// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Stack,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
} from '@mui/material';

// OUR COMPONENTS
import Loader from '../../common/Loader';
import CountrySelect from '../../common/inputs/CountrySelect';

// FORMATTED MESSAGES
import CustomerReportsFormattedMessages from '../../../messages/reports/CustomerReportsFormattedMessages';

// NETWORKING
import useAdminRequest from '../../../requests/AdminRequest';
import LanguagePicker from '../../common/LanguagePicker';

const AdminCustomerSingleView = ({
  customerId,
  setPageTitle,
  errorHandler,
  accessLevel,
  accessType,
}) => {
  const [customer, setCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const { getSingleCustomer, updateCustomer } = useAdminRequest();

  const buttonText = isEditing ? 'Cancel' : 'Edit';
  const isGodAdmin = accessLevel === '3' && accessType === '3';

  useEffect(() => {
    setPageTitle(CustomerReportsFormattedMessages.customerInformation);
    const fetchCustomer = async () => {
      const customerData = await getSingleCustomer(customerId, errorHandler);
      setCustomer(customerData);
    };

    fetchCustomer();
  }, [customerId, errorHandler]);

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleFormChange = (name, value) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      console.log(edits);
      await updateCustomer(customerId, edits, errorHandler);
      const customerData = await getSingleCustomer(customerId, errorHandler);
      setCustomer(customerData);
      setIsEditing(false);
    } catch (e) {
      console.log('handle submit err:', e);
    }
  };

  const generateFormGroup = (label, displayValue, controlId) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  if (!customer) {
    return <Loader />;
  }

  return (
    <>
      {!isEditing ? (
        <Grid container spacing={2}>
          {generateFormGroup(
            "Customer's Name:",
            customer.customer_name,
            'customer_name'
          )}
          {generateFormGroup(
            'Account Id:',
            customer.customer_account_no || '',
            'customer_account_no'
          )}
          {generateFormGroup(
            'Customer No (ABN):',
            customer.customer_abn || '',
            'customer_abn'
          )}
          {generateFormGroup(
            'Customer Reference No:',
            customer.customer_ref_no || '',
            'customer_ref_no'
          )}
          {generateFormGroup(
            'Default Originator:',
            customer.default_originator || '',
            'default_originator'
          )}
          {generateFormGroup(
            'Type - Class:',
            `${customer.customer_type} - ${customer.customer_class}`,
            'type_class'
          )}
          {generateFormGroup(
            'Status:',
            customer.customer_status === 'A' ? 'Active' : 'Inactive',
            'customer_status'
          )}
          {generateFormGroup(
            'Sales Agent:',
            customer.sales_agent_name || '',
            'sales_agent_id'
          )}
          {generateFormGroup(
            'Tariff Plan:',
            customer.tariff_plan_name || '',
            'tariff_plan_id'
          )}
          {generateFormGroup(
            'Country:',
            customer.country_name || 'Unknown',
            'country_name'
          )}
          {generateFormGroup(
            'Contract Form Id:',
            customer.contract_id || '',
            'contract_id'
          )}
          {generateFormGroup(
            'HTTP Reply Template:',
            customer.reply_template || 'None',
            'reply_template'
          )}
          {generateFormGroup(
            'HTTP Reply URL:',
            customer.reply_url || '',
            'reply_url'
          )}
          {generateFormGroup(
            'Last Billed:',
            customer.last_billed || '',
            'last_billed'
          )}
          {isGodAdmin ? (
            <Grid size={12}>
              <Button
                variant="contained"
                color="secondary"
                onClick={toggleEdit}
              >
                {buttonText}
              </Button>
            </Grid>
          ) : null}
        </Grid>
      ) : (
        <form
          onSubmit={handleSubmit}
          onChange={(e) => handleFormChange(e.target.name, e.target.value)}
        >
          <Grid container rowSpacing={2} maxWidth={800}>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  required
                  sx={{
                    width: 220,
                  }}
                >
                  Customer&rsquo;s Name
                </InputLabel>
                <TextField
                  name="customer_name"
                  size="small"
                  defaultValue={customer.customer_name}
                  fullWidth
                />
              </Stack>
            </Grid>
            {generateFormGroup(
              'Account Id:',
              customer.customer_account_no || '',
              'customer_account_no'
            )}
            {generateFormGroup(
              'Customer No (ABN):',
              customer.customer_abn || '',
              'customer_abn'
            )}
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.customerRef}
                </InputLabel>
                <TextField
                  name="customer_ref_no"
                  size="small"
                  defaultValue={customer.customer_ref_no}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.defaulOriginator}
                </InputLabel>
                <TextField
                  name="default_originator"
                  size="small"
                  defaultValue={customer.default_originator}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.typeClass}
                </InputLabel>
                <TextField
                  name="type_class"
                  size="small"
                  defaultValue={`${customer.customer_type} - ${customer.customer_class}`}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.status}
                </InputLabel>
                <Select
                  label={CustomerReportsFormattedMessages.status}
                  name="customer_status"
                  size="small"
                  defaultValue={customer.customer_status}
                  onChange={(e) =>
                    handleFormChange('customer_status', e.target.value)
                  }
                  fullWidth
                >
                  <MenuItem value="A">
                    {CustomerReportsFormattedMessages.active}
                  </MenuItem>
                  <MenuItem value="C">
                    {CustomerReportsFormattedMessages.cancelled}
                  </MenuItem>
                  <MenuItem value="P">
                    {CustomerReportsFormattedMessages.pendingCancelled}
                  </MenuItem>
                  <MenuItem value="S">
                    {CustomerReportsFormattedMessages.suspended}
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.salesAgent}
                </InputLabel>
                <TextField
                  name="sales_agent_id"
                  size="small"
                  defaultValue={customer.sales_agent_id}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.tariffPlan}
                </InputLabel>
                <TextField
                  name="tariff_plan_id"
                  size="small"
                  defaultValue={customer.tariff_plan_id}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.country}
                </InputLabel>
                <CountrySelect
                  name="country_id"
                  fullWidth
                  selectDefault
                  value={edits.country_id || customer.country_id}
                  onChange={(_, newValue) =>
                    handleFormChange('country_id', newValue)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.contractId}
                </InputLabel>
                <TextField
                  name="contract_id"
                  size="small"
                  defaultValue={customer.contract_id}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.language}
                </InputLabel>
                <LanguagePicker
                  name="language_id"
                  size="small"
                  value={edits.language_id || customer.language_id}
                  onChange={(newValue) =>
                    handleFormChange('language_id', newValue)
                  }
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active_creation"
                      defaultChecked={customer.active_creation === 'Y'}
                      onChange={(e) =>
                        handleFormChange('active_creation', e.target.checked)
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.activeCreation}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="red_alert_domain_flag"
                      defaultChecked={customer.red_alert_domain_flag === 'Y'}
                      onChange={(e) =>
                        handleFormChange(
                          'red_alert_domain_flag',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.redAlertEnabled}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sales_tax_flag"
                      defaultChecked={customer.sales_tax_flag === 'Y'}
                      onChange={(e) =>
                        handleFormChange('sales_tax_flag', e.target.checked)
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.gSTApplicable}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="oms_flag"
                      defaultChecked={customer.oms_flag === 'Y'}
                      onChange={(e) =>
                        handleFormChange('oms_flag', e.target.checked)
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.oMSFlag}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="reciept_flag"
                      defaultChecked={customer.reciept_flag === 'Y'}
                      onChange={(e) =>
                        handleFormChange('reciept_flag', e.target.checked)
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.receiptFlag}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="employee_trust_flag"
                      defaultChecked={customer.employee_trust_flag === 'Y'}
                      onChange={(e) =>
                        handleFormChange(
                          'employee_trust_flag',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={CustomerReportsFormattedMessages.employeeTrustFlag}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.messagePriority}
                </InputLabel>
                <Select
                  name="message_priority"
                  size="small"
                  fullWidth
                  defaultValue={customer.message_priority}
                  onChange={(e) =>
                    handleFormChange('message_priority', e.target.value)
                  }
                >
                  <MenuItem value="0">
                    {CustomerReportsFormattedMessages.low.props.defaultMessage.toString()}
                  </MenuItem>
                  <MenuItem value="1">
                    {CustomerReportsFormattedMessages.med.props.defaultMessage.toString()}
                  </MenuItem>
                  <MenuItem value="2">
                    {CustomerReportsFormattedMessages.high.props.defaultMessage.toString()}
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.httpReplyTempl}
                </InputLabel>
                <Select
                  name="reply_template"
                  size="small"
                  defaultValue={
                    customer.reply_template !== 'Null'
                      ? customer.reply_template || ''
                      : ''
                  }
                  onChange={(e) =>
                    handleFormChange('reply_template', e.target.value)
                  }
                  fullWidth
                >
                  <MenuItem value="">
                    {CustomerReportsFormattedMessages.none}
                  </MenuItem>
                  <MenuItem value="RO4HttpReply.txt">RO4HttpReply.txt</MenuItem>
                  <MenuItem value="RO4HttpReplyFormData.txt">
                    RO4HttpReplyFormData.txt
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: 220,
                  }}
                >
                  {CustomerReportsFormattedMessages.httpReplyUrl}
                </InputLabel>
                <TextField
                  name="reply_url"
                  size="small"
                  defaultValue={customer.reply_url}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
                {isGodAdmin ? (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={toggleEdit}
                  >
                    {buttonText}
                  </Button>
                ) : null}

                <Button variant="contained" color="secondary" type="submit">
                  {CustomerReportsFormattedMessages.save}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
};

export default AdminCustomerSingleView;
